import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  parse,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';

export const DATE_PRESETS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  TOMORROW: 'tomorrow',
  THIS_WEEK: 'this_week',
  LAST_WEEK: 'last_week',
  NEXT_WEEK: 'next_week',
  THIS_MONTH: 'this_month',
  NEXT_MONTH: 'next_month',
  NEXT_30_DAYS: 'next_30_days',
  ALL: 'all',
  UPCOMING: 'upcoming',
  PAST: 'past',
  CUSTOM: 'custom',
};

export const NAMED_PRESET_LABEL_KEYS = {
  [DATE_PRESETS.ALL]: 'ALL_DATES',
  [DATE_PRESETS.UPCOMING]: 'UPCOMING',
  [DATE_PRESETS.PAST]: 'PAST',
  [DATE_PRESETS.THIS_WEEK]: 'THIS_WEEK',
  [DATE_PRESETS.LAST_WEEK]: 'LAST_WEEK',
  [DATE_PRESETS.NEXT_WEEK]: 'NEXT_WEEK',
  [DATE_PRESETS.THIS_MONTH]: 'THIS_MONTH',
  [DATE_PRESETS.NEXT_30_DAYS]: 'NEXT_30_DAYS',
  [DATE_PRESETS.TODAY]: 'TODAY',
  [DATE_PRESETS.YESTERDAY]: 'YESTERDAY',
  [DATE_PRESETS.TOMORROW]: 'TOMORROW',
  [DATE_PRESETS.NEXT_MONTH]: 'NEXT_MONTH',
};

export const formatLocalDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseLocalDate = value => {
  if (!value) return null;
  const date = typeof value === 'string' ? parseISO(value) : value;
  return Number.isNaN(date?.getTime?.()) ? null : date;
};

export const formatInputDate = value => {
  const date = parseLocalDate(value);
  if (!date) return '';
  return format(date, 'd MMM yyyy');
};

export const parseInputDate = value => {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const fromIso = parseLocalDate(trimmed);
  if (fromIso) return fromIso;
  const parsed = parse(trimmed, 'd MMM yyyy', new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getDefaultDateRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = addDays(start, 30);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(end),
    preset: DATE_PRESETS.NEXT_30_DAYS,
  };
};

export const getPresetRange = (preset, weekStartsOn = 1) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (preset) {
    case DATE_PRESETS.TODAY: {
      return {
        start: formatLocalDate(today),
        end: formatLocalDate(today),
      };
    }
    case DATE_PRESETS.YESTERDAY: {
      const yesterday = subDays(today, 1);
      return {
        start: formatLocalDate(yesterday),
        end: formatLocalDate(yesterday),
      };
    }
    case DATE_PRESETS.TOMORROW: {
      const tomorrow = addDays(today, 1);
      return {
        start: formatLocalDate(tomorrow),
        end: formatLocalDate(tomorrow),
      };
    }
    case DATE_PRESETS.THIS_WEEK: {
      const start = startOfWeek(today, { weekStartsOn });
      const end = endOfWeek(today, { weekStartsOn });
      return { start: formatLocalDate(start), end: formatLocalDate(end) };
    }
    case DATE_PRESETS.LAST_WEEK: {
      const previous = subDays(today, 7);
      const start = startOfWeek(previous, { weekStartsOn });
      const end = endOfWeek(previous, { weekStartsOn });
      return { start: formatLocalDate(start), end: formatLocalDate(end) };
    }
    case DATE_PRESETS.NEXT_WEEK: {
      const next = addDays(today, 7);
      const start = startOfWeek(next, { weekStartsOn });
      const end = endOfWeek(next, { weekStartsOn });
      return { start: formatLocalDate(start), end: formatLocalDate(end) };
    }
    case DATE_PRESETS.THIS_MONTH: {
      const start = startOfMonth(today);
      const end = endOfMonth(today);
      return { start: formatLocalDate(start), end: formatLocalDate(end) };
    }
    case DATE_PRESETS.NEXT_MONTH: {
      const next = addMonths(today, 1);
      const start = startOfMonth(next);
      const end = endOfMonth(next);
      return { start: formatLocalDate(start), end: formatLocalDate(end) };
    }
    case DATE_PRESETS.NEXT_30_DAYS: {
      return getDefaultDateRange();
    }
    case DATE_PRESETS.ALL: {
      const end = addMonths(today, 30 * 12);
      return {
        start: '1970-01-01',
        end: formatLocalDate(end),
      };
    }
    case DATE_PRESETS.UPCOMING: {
      const end = addMonths(today, 30 * 12);
      return {
        start: formatLocalDate(today),
        end: formatLocalDate(end),
      };
    }
    case DATE_PRESETS.PAST: {
      return {
        start: '1970-01-01',
        end: formatLocalDate(subDays(today, 1)),
      };
    }
    default:
      return null;
  }
};

export const formatPresetHint = (preset, weekStartsOn = 1) => {
  const range = getPresetRange(preset, weekStartsOn);
  if (!range) return '';

  const start = parseLocalDate(range.start);
  const end = parseLocalDate(range.end);
  if (!start || !end) return '';

  switch (preset) {
    case DATE_PRESETS.THIS_WEEK:
    case DATE_PRESETS.LAST_WEEK:
    case DATE_PRESETS.NEXT_WEEK:
      return `${format(start, 'd')}–${format(end, 'd MMM')}`;
    case DATE_PRESETS.THIS_MONTH:
      return format(start, 'MMM');
    case DATE_PRESETS.YESTERDAY:
    case DATE_PRESETS.TODAY:
    case DATE_PRESETS.TOMORROW:
      return format(start, 'd MMM');
    default:
      return '';
  }
};

export const countSelectedDays = (start, end) => {
  const startDate = parseLocalDate(start);
  const endDate = parseLocalDate(end);
  if (!startDate || !endDate) return 0;
  const [from, to] =
    startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
  return differenceInCalendarDays(to, from) + 1;
};

export const formatRangeButtonLabel = (start, end) => {
  const startDate = parseLocalDate(start);
  const endDate = parseLocalDate(end);
  if (!startDate || !endDate) return '';
  return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`;
};

export const formatRangeHeading = (start, end) => {
  const startDate = parseLocalDate(start);
  const endDate = parseLocalDate(end);
  if (!startDate || !endDate) return '';

  if (startDate.getFullYear() === endDate.getFullYear()) {
    if (
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getDate() === endDate.getDate()
    ) {
      return format(startDate, 'd MMMM, yyyy');
    }
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${format(startDate, 'd')}-${format(endDate, 'd MMMM, yyyy')}`;
    }
    return `${format(startDate, 'd MMMM')} - ${format(endDate, 'd MMMM, yyyy')}`;
  }

  return `${format(startDate, 'd MMMM, yyyy')} - ${format(endDate, 'd MMMM, yyyy')}`;
};

export const shiftDateRange = (start, end, direction) => {
  const startDate = parseLocalDate(start);
  const endDate = parseLocalDate(end);
  if (!startDate || !endDate) return { start, end };

  const daySpan =
    Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
  const delta = direction * daySpan;
  return {
    start: formatLocalDate(addDays(startDate, delta)),
    end: formatLocalDate(addDays(endDate, delta)),
  };
};

const normalizeFilterValue = value => {
  if (value === null) return 'null';
  if (value === undefined || value === '') return undefined;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const PRESENCE_FILTER_OPERATORS = ['is_present', 'is_not_present'];

export const serializeFiltersForApi = filters => {
  return (filters || [])
    .map(filter => {
      let values = filter.values;
      if (Array.isArray(values)) {
        values = values.map(value =>
          value && typeof value === 'object' ? value.id : value
        );
      } else if (values && typeof values === 'object') {
        values = [values.id];
      } else if (values != null && values !== '') {
        values = [values];
      } else {
        values = [];
      }

      const operator = filter.filterOperator || filter.filter_operator;
      const serializedValues = values
        .map(normalizeFilterValue)
        .filter(v => v !== undefined);
      const queryOperator = filter.queryOperator || filter.query_operator;

      const rule = {
        attribute_key: filter.attributeKey || filter.attribute_key,
        filter_operator: operator,
        values: serializedValues,
        query_operator: queryOperator === 'or' ? 'or' : 'and',
      };

      const customType =
        filter.customAttributeType || filter.custom_attribute_type;
      if (customType) {
        rule.custom_attribute_type = customType;
      }

      return rule;
    })
    .filter(
      rule =>
        PRESENCE_FILTER_OPERATORS.includes(rule.filter_operator) ||
        rule.values.length
    );
};
